import { useEffect, useState } from 'react';

/** How often an open page asks whether a newer build is live. */
const CHECK_EVERY_MS = 5 * 60_000;

/**
 * Whether a newer build than this one is live. The page is only fetched when it
 * is opened, and a game night stays open all evening, so without this a phone
 * keeps the old app (and the old list of games) until somebody reloads it.
 */
export function useNewVersion(): boolean {
  const [newer, setNewer] = useState(false);
  useEffect(() => {
    if (newer) return;
    let live = true;
    const check = () => {
      fetch('/version.json', { cache: 'no-store' })
        .then((r) => (r.ok ? (r.json() as Promise<{ build?: string }>) : null))
        .then((v) => {
          if (live && v?.build && v.build !== __BUILD_ID__) setNewer(true);
        })
        .catch(() => {});
    };
    check();
    const timer = setInterval(check, CHECK_EVERY_MS);
    const onShow = () => document.visibilityState === 'visible' && check();
    document.addEventListener('visibilitychange', onShow);
    return () => {
      live = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onShow);
    };
  }, [newer]);
  return newer;
}

/**
 * "A new version is out", along the bottom, on screens where a reload loses
 * nothing: the front page and the game night lounge. Never in the middle of a game.
 */
export function UpdateBanner() {
  const newer = useNewVersion();
  if (!newer) return null;
  return (
    <div
      role="status"
      className="panel row"
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 'calc(12px + env(safe-area-inset-bottom))',
        zIndex: 55,
        gap: 10,
        padding: '10px 12px',
        borderColor: 'var(--green)',
        background: 'var(--ink-2)',
        boxShadow: '0 10px 26px rgba(0,0,0,0.5)',
        maxWidth: 520,
        margin: '0 auto',
      }}
    >
      <span className="grow small">🆕 A new version is out, with the latest games and fixes.</span>
      <button className="btn-primary small" style={{ minHeight: 40, flexShrink: 0 }} onClick={() => location.reload()}>
        Refresh
      </button>
    </div>
  );
}
